import { If } from 'components';
import { FC } from 'react';
import { formatDecimalThreePlaces, formattedLineItemQty, getAmountWithCurrency } from 'utils/amountUtils';
import { InvoiceByIdV2 } from '../invoices.model';

interface LineItemV2Props {
  invoice: InvoiceByIdV2;
  marginX?: string;
  paddingX?: string;
  showTotalAmount?: boolean;
  borderStyle?: string;
}

const LineItemsV2: FC<LineItemV2Props> = props => {
  const { invoice, marginX = 'mx-5', paddingX = 'px-5', showTotalAmount = true, borderStyle = '' } = props;
  const discountPercentage = invoice.discount.type === 'PERCENTAGE' ? invoice.discount.value : 0;

  return (
    <>
      <div
        className={`flex items-center justify-start py-3
            text-17px font-bold text-headingGray ${marginX}`}>
        <div>Line items ({invoice.lineItems?.length || 0})</div>
      </div>
      <div>
        <div className="flex flex-col gap-4 pt-2 pb-4">
          {invoice.lineItems?.map((lineItem, index) => (
            <div
              className={`flex justify-between border-b border-secondaryBtn last:border-b-0 ${marginX}`}
              key={lineItem.name + lineItem.qty}>
              <div className="flex w-2/3 flex-col gap-2">
                <div
                  id={`line-item-${index + 1}`}
                  className="line-clamp-8 break-words text-sbase font-semibold text-headingGray">
                  {lineItem.name}
                </div>
                <If condition={Boolean(lineItem.additionalDetails)}>
                  <div
                    id={`${lineItem.name}-${index}-additional-details`}
                    className="whitespace-pre-line text-px13 text-primaryText line-clamp-3">
                    {lineItem.additionalDetails}
                  </div>
                </If>
              </div>
              <div className="flex flex-col gap-2 whitespace-nowrap text-right">
                <div id={`${lineItem.name}-${index}-amount`} className="text-sbase font-semibold text-headingGray">
                  {getAmountWithCurrency(lineItem.amount)}
                </div>
                <div id={`${lineItem.name}-${index}-price-qnty-splitup`} className="text-px13 text-accentText">
                  {`(${formattedLineItemQty({ qtyString: lineItem.qty.toString() })} x ${getAmountWithCurrency(
                    lineItem.unitPrice
                  )})`}
                </div>
              </div>
            </div>
          ))}
        </div>
        <If condition={Boolean(invoice.discountAmount && invoice.discountAmount > 0)}>
          <div className={`flex justify-between py-4 ${marginX} text-sbase font-semibold text-tertiaryText`}>
            <div>Discount</div>
            <div id="line-item-discount" className="flex gap-4 text-right">
              {discountPercentage ? (
                <div className="text-accentText">{formatDecimalThreePlaces(discountPercentage)}%</div>
              ) : (
                ''
              )}
              <div>-{getAmountWithCurrency(invoice.discountAmount)}</div>
            </div>
          </div>
        </If>
        <div
          className={`bg-[#F5F8FB] ${paddingX} flex flex-col
          gap-2.5 py-3 text-sbase text-tertiaryText ${borderStyle}`}>
          <div className="text-sbase font-semibold text-primaryText">Summary</div>
          <div className="flex justify-between">
            <div>Subtotal</div>
            <div className="font-semibold">{getAmountWithCurrency(invoice.subTotalAmount)}</div>
          </div>
          <If condition={invoice.serviceFee?.enabled}>
            <div className="flex justify-between">
              <div>Service fee</div>
              <div id="line-items-service-fee" className="flex gap-4 font-semibold">
                <div>{getAmountWithCurrency(invoice.serviceFeeAmount)}</div>
              </div>
            </div>
          </If>
          {invoice?.serviceCharges?.length > 0 && (
            <div className="flex justify-between" key={`${invoice?.serviceCharges?.[0]?.name}`}>
              <div className="break-words line-clamp-3">{invoice?.serviceCharges?.[0]?.name}</div>
              <div id="invoice-line-items-service-fee-percent" className="flex gap-4 font-semibold">
                <div>{getAmountWithCurrency(invoice.serviceChargeAmount)}</div>
              </div>
            </div>
          )}
          {invoice?.taxes?.length > 0 &&
            invoice.taxes.map((tax, index) => (
              <div className="flex justify-between" key={`${tax.name + index}`}>
                <div className="break-words line-clamp-3">{tax.name}</div>
                <div id="line-items-additional-tax-percent" className="flex gap-4 font-semibold">
                  <div>{getAmountWithCurrency(tax.amount)}</div>
                </div>
              </div>
            ))}
        </div>
        <If condition={showTotalAmount}>
          <div className={`flex justify-between ${paddingX} bg-secondaryBtn py-3 font-bold text-headingGray`}>
            <div>Total amount</div>
            <div id="line-items-total-amount">{getAmountWithCurrency(invoice.totalAmount)}</div>
          </div>
        </If>
      </div>
    </>
  );
};

export default LineItemsV2;
